import React, { useState, useEffect, useRef } from 'react';
import DonutChart3 from '@/components/BoardPack/components/charts/DonutChart3';
import ErrorState from '@/components/common/ErrorStateComponent';
import LoadingComponent from '@/components/common/LoadingComponent';
import HeadingComponent from '@/components/BoardPack/components/shared/heading';

const KEY_MAP = {
    cash:         (yr) => yr?.cashCard?.cash?.percent_value,
    card:         (yr) => yr?.cashCard?.card?.percent_value,
    pos:          (yr) => yr?.payLocation?.POS?.percent_value,
    apm:          (yr) => yr?.payLocation?.APM?.percent_value,
    appWeb:       (yr) => yr?.payLocation?.['APP/WEB']?.percent_value,
    verifier:     (yr) => yr?.payLocation?.VERIFIER?.percent_value,
    cashPos:      (yr) => yr?.cashSourceSplit?.POS?.percent_value,
    cashApm:      (yr) => yr?.cashSourceSplit?.APM?.percent_value,
    cardPos:      (yr) => yr?.cardSourceSplit?.POS?.percent_value,
    cardApm:      (yr) => yr?.cardSourceSplit?.APM?.percent_value,
    cardAppWeb:   (yr) => yr?.cardSourceSplit?.['APP/WEB']?.percent_value,
    cardVerifier: (yr) => yr?.cardSourceSplit?.VERIFIER?.percent_value,
    apmCash:      (yr) => yr?.sourceCashCardSplit?.APM?.cash?.percent_value,
    apmCard:      (yr) => yr?.sourceCashCardSplit?.APM?.card?.percent_value,
    posCash:      (yr) => yr?.sourceCashCardSplit?.POS?.cash?.percent_value,
    posCard:      (yr) => yr?.sourceCashCardSplit?.POS?.card?.percent_value,
    pnd:          (yr) => yr?.pndAppWeb?.PND?.percent_value,
    pndAppWeb:    (yr) => yr?.pndAppWeb?.['APP/WEB']?.percent_value,
    "PND :: CARD":    (yr) => yr?.pndSplit?.['PND :: CARD']?.percent_value,
    "PND :: COINS":   (yr) => yr?.pndSplit?.['PND :: COINS']?.percent_value,
    "PND :: EWALLET": (yr) => yr?.pndSplit?.['PND :: EWALLET']?.percent_value,
    "PND :: NOTES":   (yr) => yr?.pndSplit?.['PND :: NOTES']?.percent_value,
};

const LABEL_MAP = {
    cash: 'CASH',         card: 'CARD',
    pos: 'POS',           apm: 'APM',
    appWeb: 'APP/WEB',    verifier: 'VERIFIER',
    cashPos: 'POS',       cashApm: 'APM',
    cardPos: 'POS',       cardApm: 'APM',
    cardAppWeb: 'APP/WEB',cardVerifier: 'VERIFIER',
    apmCash: 'CASH',      apmCard: 'CARD',
    posCash: 'CASH',      posCard: 'CARD',
    pnd: 'PND',           pndAppWeb: 'APP/WEB',
    coins: 'COINS',       ewallet: 'EWALLET',
    notes: 'NOTES',
};

const buildChartConfig = (chartCfg) => {
    if (!chartCfg) return { labels: [], colors: [], legend: [] };
    const { keys = [], colors = {} } = chartCfg;
    return {
        labels: keys.map(k => LABEL_MAP[k] || k),
        colors: keys.map(k => colors[k] || '#cccccc'),
        legend: keys.map(k => ({ label: LABEL_MAP[k] || k, color: colors[k] || '#cccccc' })),
    };
};

const buildChartData = (years, contractData, chartCfg) => {
    if (!chartCfg) return [];
    const { keys = [] } = chartCfg;
    return years.map((yr) => {
        const yData = contractData[yr];
        return {
            year: yr,
            values: keys.map(k => {
                const val = KEY_MAP[k]?.(yData) ?? 0;
                return Number(val.toFixed(2));
            }),
        };
    });
};

const DonutChart = ({ year = "", month = "", contract = "", page = null }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [firstChartData, setFirstChartData] = useState([]);
    const [secondChartData, setSecondChartData] = useState([]);
    const hasFetchedRef = useRef(false);
    const paramsValueType = page?.params?.value_type;

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            const apiParams = {};
            if (page?.params) {
                Object.keys(page.params).forEach((key) => {
                    let value = page.params[key];
                    if (typeof value === 'string') {
                        value = value.replace('{{year}}', year);
                        value = value.replace('{{month}}', month);
                    }
                    apiParams[key] = value;
                });
            }

            const response = await mockApiCall(apiParams);

            if (!response?.success) throw new Error(response?.message || 'Failed to fetch data');

            const contractId = response.meta?.contract;
            const contractData = response.data?.[contractId] || {};
            const years = Object.keys(contractData).sort();

            setFirstChartData(buildChartData(years, contractData, page?.chart_config?.first_chart));
            setSecondChartData(buildChartData(years, contractData, page?.chart_config?.second_chart));

        } catch (err) {
            console.error('Failed to load data:', err);
            setError(err.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // if (!year || !month || !contract) return;
        // if (hasFetchedRef.current) return;
        // hasFetchedRef.current = true;
        fetchData();
    }, [year, month, contract, paramsValueType]);

    if (loading) return <LoadingComponent />;
    if (error) return <ErrorState error={error} onRetry={fetchData} />;

    const firstCfg = buildChartConfig(page?.chart_config?.first_chart);
    const secondCfg = buildChartConfig(page?.chart_config?.second_chart);

    return (
        <div className="w-full p-5 bg-background">
            <div className="w-full mx-auto">
                <div className="flex flex-col gap-1 mb-1">
                    {page?.title && <HeadingComponent variant="main">{page.title}</HeadingComponent>}
                    {page?.subtitle && <HeadingComponent variant="sub">{page.subtitle}</HeadingComponent>}
                </div>

                <div className="bg-red-700 dark:bg-red-600 h-6 w-full" />
                <div className="bg-gray-900 dark:bg-gray-950 py-4">

                    {/* First Chart */}
                    <div className="overflow-hidden shadow-lg">
                        {page?.in_chart_content_line_1 && (
                            <h3 className="text-center font-bold text-xs mb-2 text-white">
                                {page.in_chart_content_line_1.split(/(\([^)]+\))/g).map((part, index) => {
                                    if (part.startsWith('(') && part.endsWith(')')) {
                                        return <span key={index} className="text-yellow-400">{part}</span>;
                                    }
                                    return part;
                                })}
                            </h3>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 place-items-center mx-4 py-12 bg-white dark:bg-gray-800">
                            {firstChartData.map((data) => (
                                <div key={data.year} className="flex justify-center items-center">
                                    <DonutChart3
                                        data={data.values}
                                        labels={firstCfg.labels}
                                        colors={firstCfg.colors}
                                        year={data.year}
                                        legendItems={firstCfg.legend}
                                        size={280}
                                        cutoutPercentage={65}
                                        labelPosition="outside"
                                        labelStyle="simple"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Second Chart */}
                    <div className="overflow-hidden shadow-lg">
                        {page?.in_chart_content_line_2 && (
                            <h3 className="text-center font-bold my-2 text-xs text-white">
                                {page.in_chart_content_line_2.split(/(\([^)]+\))/g).map((part, index) => {
                                    if (part.startsWith('(') && part.endsWith(')')) {
                                        return <span key={index} className="text-yellow-400">{part}</span>;
                                    }
                                    return part;
                                })}
                            </h3>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 place-items-center mx-4 py-12 bg-white dark:bg-gray-800">
                            {secondChartData.map((data) => (
                                <div key={data.year} className="flex justify-center items-center">
                                    <DonutChart3
                                        data={data.values}
                                        labels={secondCfg.labels}
                                        colors={secondCfg.colors}
                                        year={data.year}
                                        legendItems={secondCfg.legend}
                                        size={280}
                                        cutoutPercentage={65}
                                        labelPosition="outside"
                                        labelStyle="detailed"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default DonutChart;

const mockApiCall = (params) => {
    return new Promise((resolve) => {
        setTimeout(() => {
            const valueType = Number(params.value_type || 1);

            const mockDataMap = {
                "1501006": {
                    1: {
                        "2024": {
                            cashCard: {
                                cash: { value: 180000, percent_value: 40, percent: "40%" },
                                card: { value: 270000, percent_value: 60, percent: "60%" },
                            },
                            payLocation: {
                                POS: { value: 157500, percent_value: 35, percent: "35%" },
                                APM: { value: 112500, percent_value: 25, percent: "25%" },
                                "APP/WEB": { value: 45000, percent_value: 10, percent: "10%" },
                                VERIFIER: { value: 135000, percent_value: 30, percent: "30%" },
                                VERIFIER_SALIK: { value: 0, percent_value: 0, percent: "0%" },
                                VALET: { value: 0, percent_value: 0, percent: "0%" },
                            },
                            totals: { grand_total: 450000 },
                        },
                        "2025": {
                            cashCard: {
                                cash: { value: 325230, percent_value: 51.71, percent: "51.71%" },
                                card: { value: 303750, percent_value: 48.29, percent: "48.29%" },
                            },
                            payLocation: {
                                POS: { value: 290100, percent_value: 46.12, percent: "46.12%" },
                                APM: { value: 101220, percent_value: 16.09, percent: "16.09%" },
                                "APP/WEB": { value: 0, percent_value: 0, percent: "0%" },
                                VERIFIER: { value: 237660, percent_value: 37.78, percent: "37.78%" },
                                VERIFIER_SALIK: { value: 0, percent_value: 0, percent: "0%" },
                                VALET: { value: 0, percent_value: 0, percent: "0%" },
                            },
                            totals: { grand_total: 628980 },
                        },
                        "2026": {
                            cashCard: {
                                cash: { value: 4970, percent_value: 0.87, percent: "0.87%" },
                                card: { value: 563043, percent_value: 99.13, percent: "99.13%" },
                            },
                            payLocation: {
                                POS: { value: 0, percent_value: 0, percent: "0%" },
                                APM: { value: 26510, percent_value: 4.67, percent: "4.67%" },
                                "APP/WEB": { value: 89940, percent_value: 15.83, percent: "15.83%" },
                                VERIFIER: { value: 451563, percent_value: 79.50, percent: "79.50%" },
                                VERIFIER_SALIK: { value: 0, percent_value: 0, percent: "0%" },
                                VALET: { value: 0, percent_value: 0, percent: "0%" },
                            },
                            totals: { grand_total: 568013 },
                        },
                    },
                    2: {
                        "2024": {
                            cashSourceSplit: {
                                POS: { value: 130000, percent_value: 72.22, percent: "72.22%" },
                                APM: { value: 50000, percent_value: 27.78, percent: "27.78%" },
                                "APP/WEB": { value: 0, percent_value: 0, percent: "0%" },
                                VALET: { value: 0, percent_value: 0, percent: "0%" },
                                VERIFIER: { value: 0, percent_value: 0, percent: "0%" },
                                total: 180000,
                            },
                            cardSourceSplit: {
                                POS: { value: 27500, percent_value: 10.19, percent: "10.19%" },
                                APM: { value: 62500, percent_value: 23.15, percent: "23.15%" },
                                "APP/WEB": { value: 45000, percent_value: 16.67, percent: "16.67%" },
                                VERIFIER: { value: 135000, percent_value: 50, percent: "50%" },
                                VERIFIER_SALIK: { value: 0, percent_value: 0, percent: "0%" },
                                total: 270000,
                            },
                        },
                        "2025": {
                            cashSourceSplit: {
                                POS: { value: 290100, percent_value: 89.20, percent: "89.20%" },
                                APM: { value: 35130, percent_value: 10.80, percent: "10.80%" },
                                "APP/WEB": { value: 0, percent_value: 0, percent: "0%" },
                                VALET: { value: 0, percent_value: 0, percent: "0%" },
                                VERIFIER: { value: 0, percent_value: 0, percent: "0%" },
                                total: 325230,
                            },
                            cardSourceSplit: {
                                POS: { value: 0, percent_value: 0, percent: "0%" },
                                APM: { value: 66090, percent_value: 21.76, percent: "21.76%" },
                                "APP/WEB": { value: 0, percent_value: 0, percent: "0%" },
                                VERIFIER: { value: 237660, percent_value: 78.24, percent: "78.24%" },
                                VERIFIER_SALIK: { value: 0, percent_value: 0, percent: "0%" },
                                total: 303750,
                            },
                        },
                        "2026": {
                            cashSourceSplit: {
                                POS: { value: 0, percent_value: 0, percent: "0%" },
                                APM: { value: 4970, percent_value: 100, percent: "100%" },
                                "APP/WEB": { value: 0, percent_value: 0, percent: "0%" },
                                VALET: { value: 0, percent_value: 0, percent: "0%" },
                                VERIFIER: { value: 0, percent_value: 0, percent: "0%" },
                                total: 4970,
                            },
                            cardSourceSplit: {
                                POS: { value: 0, percent_value: 0, percent: "0%" },
                                APM: { value: 21540, percent_value: 3.83, percent: "3.83%" },
                                "APP/WEB": { value: 89940, percent_value: 15.97, percent: "15.97%" },
                                VERIFIER: { value: 451563, percent_value: 80.20, percent: "80.20%" },
                                VERIFIER_SALIK: { value: 0, percent_value: 0, percent: "0%" },
                                total: 563043,
                            },
                        },
                    },
                    3: {
                        "2024": {
                            sourceCashCardSplit: {
                                APM: {
                                    cash: { value: 50000, percent_value: 44.44, percent: "44.44%" },
                                    card: { value: 62500, percent_value: 55.56, percent: "55.56%" },
                                    total: 112500,
                                },
                                POS: {
                                    cash: { value: 130000, percent_value: 82.54, percent: "82.54%" },
                                    card: { value: 27500, percent_value: 17.46, percent: "17.46%" },
                                    total: 157500,
                                },
                                VERIFIER: { value: 135000, percent_value: 100, percent: "100%", total: 135000 },
                                VERIFIER_SALIK: { value: 0, percent_value: 0, percent: "0%" },
                            },
                        },
                        "2025": {
                            sourceCashCardSplit: {
                                APM: {
                                    cash: { value: 35130, percent_value: 34.71, percent: "34.71%" },
                                    card: { value: 66090, percent_value: 65.29, percent: "65.29%" },
                                    total: 101220,
                                },
                                POS: {
                                    cash: { value: 290100, percent_value: 100, percent: "100%" },
                                    card: { value: 0, percent_value: 0, percent: "0%" },
                                    total: 290100,
                                },
                                VERIFIER: { value: 237660, percent_value: 100, percent: "100%", total: 237660 },
                                VERIFIER_SALIK: { value: 0, percent_value: 0, percent: "0%" },
                            },
                        },
                        "2026": {
                            sourceCashCardSplit: {
                                APM: {
                                    cash: { value: 4970, percent_value: 18.75, percent: "18.75%" },
                                    card: { value: 21540, percent_value: 81.25, percent: "81.25%" },
                                    total: 26510,
                                },
                                POS: {
                                    cash: { value: 0, percent_value: 0, percent: "0%" },
                                    card: { value: 0, percent_value: 0, percent: "0%" },
                                    total: 0,
                                },
                                VERIFIER: { value: 451563, percent_value: 100, percent: "100%", total: 451563 },
                                VERIFIER_SALIK: { value: 0, percent_value: 0, percent: "0%" },
                            },
                        },
                    },
                },
                "1501005": {
                    4: {
                        "2024": {
                            pndAppWeb: {
                                PND: { value: 68000, percent_value: 40, percent: "40%" },
                                "APP/WEB": { value: 102000, percent_value: 60, percent: "60%" },
                                grand_total: 170000,
                            },
                            pndSplit: {
                                "PND :: CARD": { value: 64600, percent_value: 95, percent: "95%" },
                                "PND :: COINS": { value: 1360, percent_value: 2, percent: "2%" },
                                "PND :: EWALLET": { value: 680, percent_value: 1, percent: "1%" },
                                "PND :: NOTES": { value: 1360, percent_value: 2, percent: "2%" },
                                total: 68000,
                            },
                        },
                        "2025": {
                            pndAppWeb: {
                                PND: { value: 118028.03, percent_value: 49.14, percent: "49.14%" },
                                "APP/WEB": { value: 122180, percent_value: 50.86, percent: "50.86%" },
                                grand_total: 240208.03,
                            },
                            pndSplit: {
                                "PND :: CARD": { value: 117800, percent_value: 99.81, percent: "99.81%" },
                                "PND :: COINS": { value: 63.24, percent_value: 0.05, percent: "0.05%" },
                                "PND :: EWALLET": { value: 34.35, percent_value: 0.03, percent: "0.03%" },
                                "PND :: NOTES": { value: 130.44, percent_value: 0.11, percent: "0.11%" },
                                total: 118028.03,
                            },
                        },
                        "2026": {
                            pndAppWeb: {
                                PND: { value: 305299.46, percent_value: 32.15, percent: "32.15%" },
                                "APP/WEB": { value: 644310, percent_value: 67.85, percent: "67.85%" },
                                grand_total: 949609.46,
                            },
                            pndSplit: {
                                "PND :: CARD": { value: 304450, percent_value: 99.72, percent: "99.72%" },
                                "PND :: COINS": { value: 111.72, percent_value: 0.04, percent: "0.04%" },
                                "PND :: EWALLET": { value: 0, percent_value: 0, percent: "0%" },
                                "PND :: NOTES": { value: 737.74, percent_value: 0.24, percent: "0.24%" },
                                total: 305299.46,
                            },
                        },
                    },
                },
            };

            let hardcodedContract = "";
            let responseData = {};

            if (valueType === 4) {
                hardcodedContract = "1501005";
                responseData = { [hardcodedContract]: mockDataMap[hardcodedContract][valueType] };
            } else {
                hardcodedContract = "1501006";
                responseData = { [hardcodedContract]: mockDataMap[hardcodedContract][valueType] || mockDataMap[hardcodedContract][1] };
            }

            resolve({
                success: true,
                errorCode: 0,
                message: "data fetched successfully",
                meta: {
                    contract: hardcodedContract,
                    month: params.month || "May",
                    months_included: ["Jan", "Feb", "Mar", "Apr", "May"],
                    years: [2024, 2025, 2026],
                    value_type: valueType,
                },
                data: responseData,
            });
        }, 500);
    });
};
